/**
 * Full-page create for a sell-side Expression of Interest, Request for Quotation,
 * or Request for Tender. Internal prequalification gates are created here too.
 */
import React, { useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EpcClientSelect, EpcOpportunitySelect } from '@/components/epc';
import { EpcInvitationDossierFields } from '@/components/epc/commercial-ux/EpcInvitationDossierFields';
import {
  CLIENT_INVITATION_NOTE,
  EPC_INVITATION_KIND_LABEL,
  type EpcInvitationKind,
} from '@/constants/epcInvitationKind';
import { emptyInvitationDossier, type EpcInvitationDossier } from '@/constants/epcInvitationWorkflow';
import { epcCommercialService } from '@/services/epcCommercial.service';
import opportunityService from '@/services/opportunity.service';

const { Title, Text } = Typography;
const { TextArea } = Input;

type KindChoice = EpcInvitationKind | 'PQ';

function initialKind(raw: string | null): KindChoice {
  if (raw === 'EOI' || raw === 'RFQ' || raw === 'RFT' || raw === 'PQ') return raw;
  return 'RFT';
}

const EpcInvitationCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [form] = Form.useForm();
  const kind = (Form.useWatch('kind', form) || initialKind(params.get('kind'))) as KindChoice;
  const customerId = Form.useWatch('customerId', form) as string | undefined;
  const opportunityIdParam = params.get('opportunityId') || '';

  const oppQ = useQuery({
    queryKey: ['opportunity', opportunityIdParam],
    queryFn: () => opportunityService.getById(opportunityIdParam),
    enabled: Boolean(opportunityIdParam),
  });

  useEffect(() => {
    const opp = oppQ.data;
    if (!opp) return;
    form.setFieldsValue({
      opportunityId: opp.id,
      customerId: opp.customer_id || undefined,
      clientName: opp.customer_name || undefined,
      title: form.getFieldValue('title') || opp.opportunity_name,
    });
  }, [oppQ.data, form]);

  const createM = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const selected = values.kind as KindChoice;
      const due = values.dueDate ? dayjs(values.dueDate as dayjs.Dayjs).format('YYYY-MM-DD') : undefined;
      const issued = values.issuedAt ? dayjs(values.issuedAt as dayjs.Dayjs).format('YYYY-MM-DD') : undefined;
      const closing = values.closingAt ? dayjs(values.closingAt as dayjs.Dayjs).format('YYYY-MM-DD') : undefined;
      const dossier = values.dossier as EpcInvitationDossier;
      const metaJson: Record<string, unknown> = { invitation_dossier: dossier };
      if (values.customerId) metaJson.customer_id = values.customerId;
      const shared = {
        title: values.title,
        clientName: values.clientName || undefined,
        opportunityId: values.opportunityId || undefined,
        clientDocumentNo: values.clientDocumentNo || undefined,
        issuedAt: issued,
        closingAt: closing,
        timezone: values.timezone || undefined,
        submissionChannel: values.submissionChannel || undefined,
        confidentiality: values.confidentiality || undefined,
        scopeSummary: values.scopeSummary || undefined,
        siteLocation: values.siteLocation || undefined,
        clientContactName: values.clientContactName || undefined,
        metaJson,
      };
      if (selected === 'EOI' || selected === 'PQ') {
        return epcCommercialService.createPrequalification({
          ...shared,
          invitationKind: selected === 'EOI' ? 'EOI' : undefined,
          tenderGate: values.tenderGate,
          dueDate: due,
          eligibilitySummary: values.eligibilitySummary || undefined,
          consortiumIntent: values.consortiumIntent || undefined,
          conflictOfInterestDeclared: Boolean(values.conflictOfInterestDeclared),
        });
      }
      return epcCommercialService.createTender({
        ...shared,
        invitationKind: selected,
        invitationRef: values.clientDocumentNo || undefined,
        commercialModel: values.commercialModel || undefined,
        currency: values.currency || 'AED',
        bidDueAt: due,
        submissionDueAt: closing,
        quotationValidityDays: selected === 'RFQ' ? values.quotationValidityDays : undefined,
        exclusionsAssumptions: selected === 'RFQ' ? values.exclusionsAssumptions : undefined,
        deliveryBasis: selected === 'RFQ' ? values.deliveryBasis : undefined,
        technicalResponseRequired: selected === 'RFQ' ? Boolean(values.technicalResponseRequired) : undefined,
        envelopeStructure: selected === 'RFT' ? values.envelopeStructure : undefined,
        tenderSecurityRequired: selected === 'RFT' ? Boolean(values.tenderSecurityRequired) : undefined,
        technicalWeight: selected === 'RFT' ? values.technicalWeight : undefined,
        commercialWeight: selected === 'RFT' ? values.commercialWeight : undefined,
        bafoAllowed: selected === 'RFT' ? Boolean(values.bafoAllowed) : undefined,
        mandatorySiteVisit: selected === 'RFT' ? Boolean(values.mandatorySiteVisit) : undefined,
      });
    },
    onSuccess: (created, values) => {
      message.success('Invitation registered');
      qc.invalidateQueries({ queryKey: ['epc-prequalifications'] });
      qc.invalidateQueries({ queryKey: ['epc-tenders'] });
      const id = String(created?.id || '');
      const selected = (values as { kind?: KindChoice }).kind;
      if (!id) {
        navigate('/epc/commercial');
        return;
      }
      navigate(
        selected === 'EOI' || selected === 'PQ'
          ? `/epc/commercial/prequalifications/${encodeURIComponent(id)}`
          : `/epc/commercial/tenders/${encodeURIComponent(id)}`
      );
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const back =
    kind === 'RFQ' || kind === 'RFT'
      ? '/epc/commercial/tenders'
      : '/epc/commercial/prequalifications';

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <Space align="start" style={{ marginBottom: 16 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(back)} aria-label="Back" />
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {kind === 'PQ' ? 'Internal prequalification' : EPC_INVITATION_KIND_LABEL[kind]}
          </Title>
          <Text type="secondary">{CLIENT_INVITATION_NOTE}</Text>
        </div>
      </Space>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          kind: initialKind(params.get('kind')),
          opportunityId: opportunityIdParam || undefined,
          tenderGate: 'NOT_REQUIRED',
          currency: 'AED',
          consortiumIntent: 'None',
          envelopeStructure: 'TwoEnvelope',
          dossier: emptyInvitationDossier(initialKind(params.get('kind'))),
        }}
        onFinish={(values) => createM.mutate(values)}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" title="Invitation">
            <Form.Item name="kind" label="Kind">
              <Radio.Group
                onChange={(e) => {
                  const next = e.target.value as KindChoice;
                  form.setFieldValue('dossier', emptyInvitationDossier(next));
                }}
              >
                <Radio value="EOI">{EPC_INVITATION_KIND_LABEL.EOI}</Radio>
                <Radio value="RFQ">{EPC_INVITATION_KIND_LABEL.RFQ}</Radio>
                <Radio value="RFT">{EPC_INVITATION_KIND_LABEL.RFT}</Radio>
                <Radio value="PQ">Internal prequalification gate</Radio>
              </Radio.Group>
            </Form.Item>
            {kind === 'EOI' ? (
              <Alert
                type="info"
                showIcon
                message="An Expression of Interest qualifies interest. It does not request a price. Continue it to an RFQ or RFT after the outcome is known. A shortlist is preferred and is not mandatory."
              />
            ) : null}
            {kind === 'RFQ' ? (
              <Alert
                type="info"
                showIcon
                message="A Request for Quotation prices a defined scope. Supplier quotations for missing materials stay in Procurement and are linked from estimating."
              />
            ) : null}
            {kind === 'RFT' ? (
              <Alert
                type="info"
                showIcon
                message="A Request for Tender is the formal bid: technical proposal, commercial proposal, committee evaluation, and award. BAFO is allowed only on an RFT."
              />
            ) : null}
            {kind === 'PQ' ? (
              <Alert
                type="info"
                showIcon
                message="An internal prequalification gate is not a client invitation and does not replace the current EOI, RFQ, or RFT."
              />
            ) : null}
          </Card>

          <Card size="small" title="Identification">
            <Form.Item name="title" label="Subject" rules={[{ required: true, message: 'Subject is required' }]}>
              <Input />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="customerId" label="Client">
                  <EpcClientSelect
                    onClientChange={(cid, name) => {
                      form.setFieldsValue({
                        customerId: cid,
                        clientName: name || undefined,
                        opportunityId: undefined,
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item name="clientName" hidden>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="opportunityId" label="Opportunity">
                  <EpcOpportunitySelect
                    customerId={customerId}
                    onOpportunityChange={(opp) => {
                      if (!opp) return;
                      form.setFieldsValue({
                        opportunityId: opp.id,
                        customerId: opp.customer_id || form.getFieldValue('customerId'),
                        clientName: opp.customer_name || form.getFieldValue('clientName'),
                        title: form.getFieldValue('title') || opp.opportunity_name,
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="clientDocumentNo" label="Client document number">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="timezone" label="Timezone">
                  <Input placeholder="Asia/Dubai" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="scopeSummary" label="Scope summary" rules={kind === 'RFT' ? [{ required: true, message: 'Scope is required for an RFT' }] : []}>
              <TextArea rows={3} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="siteLocation" label="Site / location">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="clientContactName" label="Client contact">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="issuedAt" label="Issued">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="dueDate" label={kind === 'RFQ' || kind === 'RFT' ? 'Bid due' : 'Due'}>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="closingAt" label="Closing">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="submissionChannel" label="Submission channel">
                  <Select
                    allowClear
                    options={['Portal', 'Email', 'Hard copy', 'Mixed'].map((value) => ({ value, label: value }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="confidentiality" label="Confidentiality">
                  <Select
                    allowClear
                    options={['Public', 'Confidential', 'Restricted'].map((value) => ({ value, label: value }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {kind === 'EOI' || kind === 'PQ' ? (
            <Card size="small" title={kind === 'EOI' ? 'Expression of Interest controls' : 'Gate'}>
              <Form.Item name="tenderGate" label="Tender gate">
                <Select
                  options={[
                    { value: 'NOT_REQUIRED', label: 'Not required' },
                    { value: 'WARNING', label: 'Warning' },
                    { value: 'BLOCK', label: 'Block' },
                    { value: 'PASSED', label: 'Passed' },
                  ]}
                />
              </Form.Item>
              {kind === 'EOI' ? (
                <>
                  <Form.Item name="eligibilitySummary" label="Eligibility summary">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="consortiumIntent" label="Consortium or JV intent">
                    <Select
                      options={['None', 'JV', 'Consortium', 'Subcontract'].map((value) => ({ value, label: value }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="conflictOfInterestDeclared"
                    label="Conflict of interest acknowledged"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </>
              ) : null}
            </Card>
          ) : (
            <Card size="small" title={kind === 'RFQ' ? 'Quotation basis' : 'Tender controls'}>
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="currency" label="Currency">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="commercialModel" label="Commercial model">
                    <Select
                      allowClear
                      options={['LumpSum', 'UnitRate', 'CostPlus', 'Hybrid'].map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                </Col>
                {kind === 'RFQ' ? (
                  <Col xs={24} md={8}>
                    <Form.Item name="quotationValidityDays" label="Quotation validity (days)">
                      <Input />
                    </Form.Item>
                  </Col>
                ) : null}
              </Row>
              {kind === 'RFQ' ? (
                <>
                  <Form.Item name="deliveryBasis" label="Delivery basis">
                    <Input />
                  </Form.Item>
                  <Form.Item name="exclusionsAssumptions" label="Exclusions and assumptions">
                    <TextArea rows={3} />
                  </Form.Item>
                  <Form.Item
                    name="technicalResponseRequired"
                    label="Technical response required"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </>
              ) : (
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="envelopeStructure" label="Envelope">
                      <Select
                        options={[
                          { value: 'Single', label: 'Single' },
                          { value: 'TwoEnvelope', label: 'Two envelope' },
                          { value: 'TwoStage', label: 'Two stage' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="technicalWeight" label="Technical weight %">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="commercialWeight" label="Commercial weight %">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="tenderSecurityRequired" label="Tender security required" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name="mandatorySiteVisit" label="Mandatory site visit" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name="bafoAllowed" label="Best and final offer permitted" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>
          )}

          <EpcInvitationDossierFields kind={kind} />

          <Space>
            <Button type="primary" htmlType="submit" loading={createM.isPending}>
              Register
            </Button>
            <Button onClick={() => navigate(back)}>Cancel</Button>
          </Space>
        </Space>
      </Form>
    </div>
  );
};

export default EpcInvitationCreatePage;
